import type { AxiosError } from 'axios';
import { api } from './api-client';
import type { MyRequestItem, RequestType } from '@/hooks/useMyRequests';

interface BackendTask {
  id: string;
  taskNumber?: string;
  bizType?: string;
  title?: string;
  status?: string;
  amount?: number | string | null;
  createdAt: string;
}

const TYPE_TO_BIZ: Record<RequestType, string> = {
  leave: 'LEAVE',
  expense: 'EXPENSE',
  travel: 'TRAVEL',
  overtime: 'OVERTIME',
  makeup_clock: 'MAKEUP_CLOCK',
};

const BIZ_TO_TYPE: Record<string, RequestType> = {
  LEAVE: 'leave',
  EXPENSE: 'expense',
  TRAVEL: 'travel',
  OVERTIME: 'overtime',
  MAKEUP_CLOCK: 'makeup_clock',
};

function statusToState(status: string | undefined): MyRequestItem['state'] {
  if (status === 'APPROVED') return 'approved';
  if (status === 'REJECTED' || status === 'CANCELLED') return 'rejected';
  return 'pending';
}

function formatSubmittedAt(iso: string): string {
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const mins = Math.round(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function adapt(task: BackendTask): MyRequestItem {
  const type = BIZ_TO_TYPE[task.bizType ?? ''] ?? 'leave';
  return {
    id: task.id,
    type,
    title: task.title ?? task.taskNumber ?? '(no title)',
    submittedAt: formatSubmittedAt(task.createdAt),
    state: statusToState(task.status),
  };
}

export async function fetchMyRequests(): Promise<MyRequestItem[]> {
  const { data } = await api.get<BackendTask[]>('/approval/tasks/my-submissions');
  return data.map(adapt);
}

async function stableBizId(input: {
  type: RequestType;
  title: string;
  description: string;
  amount?: number;
}): Promise<string> {
  const contentKey = `${input.type}:${input.title}:${input.description}:${input.amount ?? ''}`;
  const subtle: SubtleCrypto | undefined =
    typeof crypto !== 'undefined' && crypto.subtle ? crypto.subtle : undefined;
  if (subtle) {
    const encoder = new TextEncoder();
    const hashBuffer = await subtle.digest('SHA-256', encoder.encode(contentKey));
    const bytes = Array.from(new Uint8Array(hashBuffer));
    const hex = bytes.slice(0, 16).map((b) => b.toString(16).padStart(2, '0')).join('');
    return `mobile-${hex}`;
  }
  let h1 = 0x811c9dc5;
  let h2 = 0xdeadbeef;
  for (let i = 0; i < contentKey.length; i++) {
    const ch = contentKey.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  const hex = ((h1 >>> 0).toString(16).padStart(8, '0') +
    (h2 >>> 0).toString(16).padStart(8, '0'));
  return `mobile-${hex}`;
}

export async function submitRequest(input: {
  type: RequestType;
  title: string;
  description: string;
  amount?: number;
}): Promise<MyRequestItem> {
  const bizType = TYPE_TO_BIZ[input.type];
  const bizId = await stableBizId(input);
  const body = {
    bizType,
    bizId,
    title: input.title,
    summary: input.description,
    ...(input.amount !== undefined ? { amount: input.amount } : {}),
  };
  try {
    const { data } = await api.post<BackendTask>('/approval/submit', body);
    return adapt(data);
  } catch (err) {
    const ax = err as AxiosError<{ message?: string }>;
    const status = ax.response?.status;
    const msg = ax.response?.data?.message ?? '';
    if (
      (input.type === 'overtime' || input.type === 'makeup_clock') &&
      ((status !== undefined && status >= 400 && status < 500) ||
        /flow.*not.*found|no.*flow|not.*configured/i.test(msg))
    ) {
      throw new Error('overtime_not_supported');
    }
    throw err;
  }
}
